import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  CardMedia,
  Rating,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  Switch,
  FormControlLabel,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Event as EventIcon,
  Image as ImageIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Share as ShareIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getRecipeById, Recipe, RecipeIngredient, calculatePortions } from '../services/recipesService';
import { FeatureLockedOverlay } from '../components/subscription/FeatureLockedOverlay';
import { useNavigate, useParams } from 'react-router-dom';
import { shareRecipeByUserId } from '../services/notificationsService';
import { ShareRecipeDialog } from '../components/recipes/ShareRecipeDialog';
import { getUserEvents, updateEvent, Event } from '../services/eventsService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import { updateRecipe, setRecipePublic } from '../services/recipesService';
import { useSafeArea } from '../hooks/useSafeArea';

export const RecipeDetailsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, hostlyUser, loading: authLoading } = useAuth();
  const { canAccessFeature } = useSubscription();
  const { top: safeAreaTop } = useSafeArea();

  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [adjustedServings, setAdjustedServings] = useState<number | null>(null);
  const [adjustedIngredients, setAdjustedIngredients] = useState<RecipeIngredient[] | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [eventsUsingRecipe, setEventsUsingRecipe] = useState<Event[]>([]);
  const [addToEventDialog, setAddToEventDialog] = useState(false);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [addingToEvents, setAddingToEvents] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [publishing, setPublishing] = useState(false);

  const isOwner = !!recipe && recipe.userId === user?.uid;

  const handleTogglePublic = async (makePublic: boolean) => {
    if (!user?.uid || !recipe) return;
    setPublishing(true);
    try {
      const authorName = `${hostlyUser?.name || ''} ${hostlyUser?.lastName || ''}`.trim() || 'Anônimo';
      await setRecipePublic(user.uid, recipe.id, makePublic, authorName);
      setRecipe(prev => prev ? { ...prev, isPublic: makePublic, authorName } : prev);
      setSnackbar({ open: true, message: makePublic ? 'Receita publicada no livro público' : 'Receita removida do livro público', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao publicar', severity: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  const navigate = useNavigate();
  const { recipeId } = useParams<{ recipeId: string }>();

  useEffect(() => {
    const loadRecipe = async () => {
      if (!user?.uid || !recipeId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const recipeData = await getRecipeById(user.uid, recipeId);
        setRecipe(recipeData);
      } catch (error) {
        console.error('Erro ao carregar receita:', error);
        setRecipe(null);
      } finally {
        setLoading(false);
      }
    };

    loadRecipe();
  }, [user?.uid, recipeId]);

  useEffect(() => {
    const loadEventsUsingRecipe = async () => {
      if (!user?.uid || !recipeId) return;

      try {
        const events = await getUserEvents(user.uid);
        const eventsWithRecipe = events.filter(event =>
          event.recipes?.includes(recipeId)
        );
        setEventsUsingRecipe(eventsWithRecipe);
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
        setEventsUsingRecipe([]);
      }
    };

    loadEventsUsingRecipe();
  }, [user?.uid, recipeId]);

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handleShareRecipe = () => {
    if (!canAccessFeature('recipe-sharing')) {
      handleUpgrade();
      return;
    }

    setShareDialogOpen(true);
  };

  const handleOpenAddToEvent = async () => {
    if (!user?.uid || !recipeId) return;
    try {
      const events = await getUserEvents(user.uid);
      setUserEvents(events);
      setSelectedEvents(events.filter(e => e.recipes?.includes(recipeId)).map(e => e.id));
      setAddToEventDialog(true);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar eventos', severity: 'error' });
    }
  };

  const handleToggleEvent = (eventId: string) => {
    setSelectedEvents(prev =>
      prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
    );
  };

  const handleAddToEvents = async () => {
    if (!recipeId) return;
    try {
      setAddingToEvents(true);
      await Promise.all(userEvents.map(async (event) => {
        const current = event.recipes || [];
        const has = current.includes(recipeId);
        const should = selectedEvents.includes(event.id);
        if (has && !should) {
          await updateEvent(event.id, { recipes: current.filter(id => id !== recipeId) });
        } else if (!has && should) {
          await updateEvent(event.id, { recipes: [...current, recipeId] });
        }
      }));
      if (user?.uid) {
        const events = await getUserEvents(user.uid);
        setEventsUsingRecipe(events.filter(e => e.recipes?.includes(recipeId)));
      }
      setSnackbar({
        open: true,
        message: selectedEvents.length > 0 ? `Receita vinculada a ${selectedEvents.length} evento(s)!` : 'Receita removida dos eventos',
        severity: 'success',
      });
      setAddToEventDialog(false);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao vincular receita aos eventos', severity: 'error' });
    } finally {
      setAddingToEvents(false);
    }
  };

  const handleConfirmShare = async (userId: string, userName: string) => {
    if (!recipe || !user) return;

    try {
      const success = await shareRecipeByUserId(
        recipe,
        user.uid,
        user.displayName || user.email || 'Um usuário',
        userId
      );

      if (success) {
        setSnackbar({ open: true, message: `Receita compartilhada com ${userName}!`, severity: 'success' });
        setShareDialogOpen(false);
      } else {
        setSnackbar({ open: true, message: 'Erro ao compartilhar receita', severity: 'error' });
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
      setSnackbar({ open: true, message: 'Erro ao compartilhar receita', severity: 'error' });
    }
  };

  const handlePortionAdjust = (newServings: number) => {
    if (!recipe || !canAccessFeature('portion-calculator')) return;

    if (newServings === recipe.servings) {
      setAdjustedServings(null);
      setAdjustedIngredients(null);
    } else {
      setAdjustedServings(newServings);
      setAdjustedIngredients(calculatePortions(recipe, newServings));
    }
  };

  const handleVideoUploadClick = () => {
    if (recipe?.videoUrl) {
      window.open(recipe.videoUrl, '_blank');
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user || !recipe) return;

    if (file.size > 50 * 1024 * 1024) {
      alert('O vídeo deve ter no máximo 50MB');
      return;
    }

    if (!file.type.startsWith('video/')) {
      alert('Por favor, selecione um arquivo de vídeo válido');
      return;
    }

    try {
      setUploadingVideo(true);
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `recipes/${user.uid}/videos/${fileName}`);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await updateRecipe(user.uid, recipe.id, { videoUrl: downloadURL });

      setRecipe(prev => prev ? { ...prev, videoUrl: downloadURL } : null);
      setSnackbar({ open: true, message: 'Vídeo enviado com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao enviar vídeo:', error);
      setSnackbar({ open: true, message: 'Erro ao enviar vídeo', severity: 'error' });
    } finally {
      setUploadingVideo(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const displayIngredients = adjustedIngredients || recipe?.ingredients || [];
  const displayServings = adjustedServings || recipe?.servings || 1;

  // Estilos tipados
  const containerStyles: SxProps<Theme> = {
    flex: 1,
    pb: 10,
    overflowY: 'auto',
  };

  const imageBoxStyles: SxProps<Theme> = {
    position: 'relative',
    mb: 3
  };

  const infoPaperStyles: SxProps<Theme> = {
    p: 2,
    borderRadius: theme.radii.medium,
    my: 2,
    backgroundColor: theme.colors.card,
    border: `1px solid ${theme.colors.border}`,
  };

  const instructionsPaperStyles: SxProps<Theme> = {
    p: 2,
    borderRadius: theme.radii.medium,
    my: 2,
    backgroundColor: theme.colors.card,
    border: `1px solid ${theme.colors.border}`,
    position: 'relative',
  };

  const videoButtonStyles: SxProps<Theme> = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    borderRadius: theme.radii.medium,
    opacity: !canAccessFeature('video-recipes') ? 0.3 : 1,
    '&:hover': {
      backgroundColor: theme.colors.primary,
      opacity: !canAccessFeature('video-recipes') ? 0.3 : 0.8,
    },
    '&:disabled': {
      backgroundColor: theme.colors.primary,
      color: 'white',
    },
  };

  const buttonBaseStyles: SxProps<Theme> = {
    borderRadius: theme.radii.medium,
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  };

  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  if (!recipe) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Typography sx={{ color: theme.colors.text.secondary }}>
          Receita não encontrada
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={containerStyles}>
        <Container sx={{ maxWidth: '400px !important' }}>
          {/* Imagem da receita */}
          <Box sx={imageBoxStyles}>
            <CardMedia
              component="img"
              height="300"
              image={recipe.imageUrl || '/resources/default-recipe.jpg'}
              alt={recipe.title}
              sx={{
                backgroundColor: theme.colors.secondary,
                objectFit: 'cover',
                borderRadius: theme.radii.medium,
              }}
            />
            <IconButton
              onClick={() => setImageDialogOpen(true)}
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                color: theme.colors.text.secondary,
                backgroundColor: `${theme.colors.background}B3`,
                '&:hover': {
                  backgroundColor: theme.colors.background,
                }
              }}
            >
              <ImageIcon />
            </IconButton>
          </Box>

          {/* Título */}
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              color: theme.colors.text.primary,
              fontWeight: 'bold',
              mb: recipe.sharedBy ? 1 : 3,
            }}
          >
            {recipe.title}
          </Typography>

          {/* Enviada por */}
          {recipe.sharedBy && (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: theme.colors.text.secondary,
                fontStyle: 'italic',
                mb: 3,
              }}
            >
              Enviada por {recipe.sharedBy}
            </Typography>
          )}

          {/* Informações principais */}
          <Paper
            variant="outlined"
            sx={infoPaperStyles}
          >
            <Grid container spacing={2}>
              <Grid size={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: theme.colors.text.primary, mb: 1 }}
                >
                  Ingredientes:
                </Typography>
                <List dense>
                  {displayIngredients.map((item: RecipeIngredient, index: number) => (
                    <ListItem key={index} sx={{ p: 0 }}>
                      <ListItemIcon sx={{ minWidth: '20px' }}>
                        <FiberManualRecordIcon
                          sx={{ fontSize: 8, color: theme.colors.text.primary }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${item.quantity} ${item.unit} ${item.name}`}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: adjustedServings ? theme.colors.primary : theme.colors.text.primary,
                            fontWeight: adjustedServings ? 'bold' : 'normal',
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid size={6}>
                <Box sx={{ mb: 2, position: 'relative' }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ color: theme.colors.text.primary, mb: 1 }}
                  >
                    Quantidade para:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon sx={{ color: theme.colors.text.secondary }} />

                    {canAccessFeature('portion-calculator') ? (
                      <TextField
                        type="number"
                        value={displayServings}
                        onChange={(e) => handlePortionAdjust(parseInt(e.target.value) || 1)}
                        size="small"
                        sx={{
                          width: 60,
                          '& .MuiInputBase-root': {
                            fontSize: '0.9rem',
                          }
                        }}
                        slotProps={{
                          htmlInput: { min: 1, max: 100 }
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: theme.colors.text.primary }}>
                        {displayServings}
                      </Typography>
                    )}

                    <Typography sx={{ color: theme.colors.text.primary }}>
                      {displayServings === 1 ? 'pessoa' : 'pessoas'}
                    </Typography>
                  </Box>

                  {!canAccessFeature('portion-calculator') && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        onClick={handleUpgrade}
                        sx={{
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          color: theme.colors.secondary,
                          p: 0,
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          }
                        }}
                      >
                        Ajustar porções (Basic)
                      </Button>
                    </Box>
                  )}

                  {adjustedServings && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        onClick={() => handlePortionAdjust(recipe?.servings || 1)}
                        sx={{
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          color: theme.colors.text.secondary,
                          p: 0,
                          minWidth: 'auto',
                        }}
                      >
                        Resetar quantidades
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ color: theme.colors.text.primary }}
                  >
                    Tempo de preparo:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ mr: 1, color: theme.colors.text.secondary }} />
                    <Typography sx={{ color: theme.colors.text.primary }}>
                      {recipe?.prepTime} minutos
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Modo de preparo */}
          <Paper
            variant="outlined"
            sx={instructionsPaperStyles}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: theme.colors.text.primary, mb: 1 }}
            >
              Modo de preparo:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.colors.text.secondary,
                mb: 2,
                whiteSpace: 'pre-line',
              }}
            >
              {recipe.instructions}
            </Typography>

            <Box sx={{ position: 'relative' }}>
              <input
                type="file"
                accept="video/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                fullWidth
                startIcon={uploadingVideo ? <CircularProgress size={24} color="inherit" /> : <PlayCircleOutlineIcon />}
                disabled={!canAccessFeature('video-recipes') || uploadingVideo}
                onClick={handleVideoUploadClick}
                sx={videoButtonStyles}
              >
                {uploadingVideo ? 'Enviando...' : recipe.videoUrl ? 'Ver vídeo' : 'Upload de vídeo'}
              </Button>
              {!canAccessFeature('video-recipes') && (
                <Box sx={{ mt: 2 }}>
                  <FeatureLockedOverlay
                    feature="Vídeos de Receitas"
                    requiredPlan="pro"
                    onUpgrade={handleUpgrade}
                    variant="card"
                  />
                </Box>
              )}
            </Box>
          </Paper>

          {/* Botões de ação */}
          <Grid container spacing={2} sx={{ my: 2 }}>
            <Grid size={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<EventIcon />}
                onClick={handleOpenAddToEvent}
                sx={{
                  ...buttonBaseStyles,
                  backgroundColor: theme.colors.secondary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.colors.secondary,
                    opacity: 0.8,
                  },
                }}
              >
                Adicionar a evento
              </Button>
            </Grid>
            <Grid size={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleShareRecipe}
                disabled={!canAccessFeature('recipe-sharing')}
                sx={{
                  ...buttonBaseStyles,
                  borderColor: theme.colors.primary,
                  color: canAccessFeature('recipe-sharing') ? theme.colors.primary : theme.colors.text.secondary,
                  opacity: !canAccessFeature('recipe-sharing') ? 0.5 : 1,
                  '&:hover': {
                    borderColor: theme.colors.primary,
                    backgroundColor: `${theme.colors.primary}10`,
                  },
                }}
              >
                Compartilhar
              </Button>
            </Grid>
          </Grid>

          {isOwner && (
            <Paper
              variant="outlined"
              sx={{ mt: 2, p: 2, borderRadius: theme.radii.medium, backgroundColor: theme.colors.card, borderColor: theme.colors.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: theme.colors.text.primary }}>Livro de receitas público</Typography>
                <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                  Publicada, qualquer pessoa pode ver e copiar pro livro dela.
                </Typography>
              </Box>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={recipe?.isPublic === true}
                    disabled={publishing}
                    onChange={(e) => handleTogglePublic(e.target.checked)}
                  />
                }
                label={recipe?.isPublic ? 'Pública' : 'Privada'}
              />
            </Paper>
          )}

          {/* Histórico e classificação */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: theme.radii.medium,
              my: 2,
              backgroundColor: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid size={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: theme.colors.text.primary, mb: 1 }}
                >
                  Histórico da receita
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: theme.colors.text.secondary }} />
                  <Typography variant="body2" sx={{ color: theme.colors.text.primary }}>
                    Usada em {eventsUsingRecipe.length} {eventsUsingRecipe.length === 1 ? 'evento' : 'eventos'}
                  </Typography>
                </Box>
                {eventsUsingRecipe.length > 0 && (
                  <List dense sx={{ mt: 1 }}>
                    {eventsUsingRecipe.map((event) => (
                      <ListItem
                        key={event.id}
                        sx={{
                          p: 0.5,
                          cursor: 'pointer',
                          borderRadius: theme.radii.small,
                          '&:hover': {
                            backgroundColor: `${theme.colors.primary}10`,
                          }
                        }}
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        <ListItemIcon sx={{ minWidth: '30px' }}>
                          <EventIcon sx={{ fontSize: '1rem', color: theme.colors.secondary }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={event.title}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: '0.875rem',
                              color: theme.colors.text.primary
                            }
                          }}
                        />
                        <ChevronRightIcon sx={{ fontSize: '1rem', color: theme.colors.text.secondary }} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Grid>
              <Grid size={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: theme.colors.text.primary }}
                >
                  Classificação
                </Typography>
                <Rating
                  name="recipe-rating"
                  value={recipe.rating || 0}
                  precision={0.5}
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: '#d9a520',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Observações */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: theme.radii.medium,
              my: 2,
              backgroundColor: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: theme.colors.text.primary, mb: 1, textDecoration: 'none' }}
            >
              Observações:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.colors.text.secondary, textDecoration: 'none' }}
            >
              {recipe.description || 'Nenhuma observação adicionada.'}
            </Typography>
          </Paper>
        </Container>
      </Box>

      <ShareRecipeDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onShare={handleConfirmShare}
      />

      {/* Adicionar a evento(s) */}
      <Dialog open={addToEventDialog} onClose={() => !addingToEvents && setAddToEventDialog(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary, mb: 0.5 }}>
            Adicionar a evento
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
            Selecione os eventos que vão usar esta receita.
          </Typography>
          {userEvents.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, py: 2, textAlign: 'center' }}>
              Você ainda não tem eventos.
            </Typography>
          ) : (
            <List sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {userEvents.map(ev => {
                const checked = selectedEvents.includes(ev.id);
                return (
                  <ListItem
                    key={ev.id}
                    onClick={() => handleToggleEvent(ev.id)}
                    sx={{
                      cursor: 'pointer', borderRadius: theme.radii.medium, mb: 0.5,
                      border: `1px solid ${checked ? theme.colors.primary : theme.colors.border}`,
                      backgroundColor: checked ? `${theme.colors.primary}12` : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={ev.title}
                      secondary={ev.date ? new Date(ev.date).toLocaleDateString('pt-BR') : ''}
                      primaryTypographyProps={{ fontWeight: checked ? 700 : 500, color: theme.colors.text.primary }}
                    />
                    {checked && <ChevronRightIcon sx={{ color: theme.colors.primary }} />}
                  </ListItem>
                );
              })}
            </List>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={() => setAddToEventDialog(false)} disabled={addingToEvents} sx={{ color: theme.colors.text.secondary }}>
              Cancelar
            </Button>
            <Button
              variant="contained" onClick={handleAddToEvents} disabled={addingToEvents}
              sx={{ backgroundColor: theme.colors.primary }}
            >
              {addingToEvents ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Full-Screen Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            boxShadow: 'none',
            margin: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '100vw',
            height: '100vh',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: 'absolute',
              top: 16 + safeAreaTop,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={recipe?.imageUrl || '/resources/default-recipe.jpg'}
            alt={recipe?.title}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
